/**
 * LocationPickerSheet — iter322 premium multi-country bottom sheet.
 *
 * Replaces the dark system <select> that was previously used by
 * `<NearMeStateCard>`. Same component is also wired (Phase B) into
 * the Shop filter bar + the Post Ad "Where is this item/service
 * located?" step.
 *
 * Distinct from the legacy `<LocationSelector>` (city-only dropdown
 * used by `UnifiedFilterSheet` on Shop) — that one will be migrated
 * in Phase B; this iteration adds the new picker without breaking
 * the old one.
 *
 * UX contract (per operator's spec):
 *   • Premium custom sheet, NOT a system menu.
 *   • Search input pinned to the top.
 *   • "Use my current location" button right under it (GPS).
 *   • Cascading lists: Country → Region/Governorate → City → Area.
 *     The user can stop at ANY level (country only is valid for
 *     Near You).
 *   • GPS prefills + shows a confirmation banner (the user MUST tap
 *     Apply — no silent save).
 *   • Mobile-first, bilingual (EN+AR), RTL safe, no overflow at
 *     420×900.
 *
 * Props:
 *   open                bool
 *   value               { country, region, city, area, latitude, longitude,
 *                         locationSource, locationConfirmed, locationLabel } | null
 *   onApply             (loc) => void  — locationConfirmed:true.
 *   onClose             () => void
 *   onClear             () => void
 *   testid              string  — root data-testid (default 'location-picker-sheet').
 *   showGpsButton       bool    — default true. Switch off for cases where the
 *                                  caller wants pure manual selection.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  MapPin, Search, X, RefreshCw, Trash2, Check, ChevronRight,
  AlertCircle, CheckCircle2,
} from 'lucide-react';
import { COUNTRIES, getCountry, getRegion, formatLocationLabel } from '../data/locations';
import { reverseGeocode as runReverseGeocode } from '../utils/reverseGeocode';
import './LocationPickerSheet.css';

const EMPTY = {
  country: '', region: '', city: '', area: '',
  // iter337 — Optional level-5 free-text. Display only.
  extra: '',
  latitude: null, longitude: null,
  locationSource: null, locationConfirmed: false,
};

function normalize(s) {
  return (s || '').toString().toLowerCase().normalize('NFKD')
    .replace(/[^a-z0-9\u0600-\u06FF]+/g, ' ')
    .trim();
}

function matchCountry(code, name) {
  if (!code && !name) return '';
  const upper = (code || '').toUpperCase();
  const byCode = COUNTRIES.find((c) => c.countryCode === upper);
  if (byCode) return byCode.countryCode;
  if (!name) return '';
  const n = normalize(name);
  const fuzzy = COUNTRIES.find(
    (c) => normalize(c.countryName).includes(n) || n.includes(normalize(c.countryName))
  );
  return fuzzy ? fuzzy.countryCode : '';
}

function matchRegionName(countryCode, regionGuess) {
  if (!countryCode || !regionGuess) return '';
  const country = getCountry(countryCode);
  if (!country) return '';
  const n = normalize(regionGuess);
  const r = country.regions.find(
    (reg) => normalize(reg.name).includes(n) || n.includes(normalize(reg.name))
  );
  return r ? r.name : '';
}

function matchCityName(countryCode, regionName, cityGuess) {
  if (!countryCode || !regionName || !cityGuess) return '';
  const region = getRegion(countryCode, regionName);
  if (!region) return '';
  const n = normalize(cityGuess);
  const c = region.cities.find(
    (city) => normalize(city.name).includes(n) || n.includes(normalize(city.name))
  );
  return c ? c.name : '';
}

export default function LocationPickerSheet({
  open,
  value,
  onApply,
  onClose,
  onClear,
  testid = 'location-picker-sheet',
  showGpsButton = true,
}) {
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.language || '').startsWith('ar');
  const [draft, setDraft] = useState(() => ({ ...EMPTY, ...(value || {}) }));
  const [search, setSearch] = useState('');
  const [gpsStatus, setGpsStatus] = useState('idle'); // idle | requesting | granted | denied | error
  const [gpsBanner, setGpsBanner] = useState(null);
  const sheetRef = useRef(null);

  // Reset on open / when the parent passes a new value.
  useEffect(() => {
    if (open) {
      setDraft({ ...EMPTY, ...(value || {}) });
      setSearch('');
      setGpsStatus('idle');
      setGpsBanner(null);
    }
  }, [open, value]);

  // Lock body scroll while the sheet is open (mobile UX).
  // iter322 — Also set a `data-modal-open` flag so siblings like the
  // PWA install banner can hide themselves and not steal taps on
  // our footer (Apply / Cancel / Clear) at 420×900.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.setAttribute('data-modal-open', 'location-picker');
    return () => {
      document.body.style.overflow = prevOverflow;
      if (document.body.getAttribute('data-modal-open') === 'location-picker') {
        document.body.removeAttribute('data-modal-open');
      }
    };
  }, [open]);

  const country = useMemo(() => getCountry(draft.country), [draft.country]);
  const region = useMemo(() => getRegion(draft.country, draft.region), [draft.country, draft.region]);
  const city = useMemo(() => {
    if (!region) return null;
    return region.cities.find((c) => c.name === draft.city) || null;
  }, [region, draft.city]);

  // Filtered lists honour the search box at the current stage.
  const filteredCountries = useMemo(() => {
    const q = normalize(search);
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        normalize(c.countryName).includes(q) ||
        normalize(c.countryNameAr).includes(q) ||
        c.countryCode.toLowerCase().includes(q)
    );
  }, [search]);

  const filteredRegions = useMemo(() => {
    if (!country) return [];
    const q = normalize(search);
    if (!q) return country.regions;
    return country.regions.filter(
      (r) => normalize(r.name).includes(q) || (r.ar && normalize(r.ar).includes(q))
    );
  }, [country, search]);

  const filteredCities = useMemo(() => {
    if (!region) return [];
    const q = normalize(search);
    if (!q) return region.cities;
    return region.cities.filter(
      (c) => normalize(c.name).includes(q) || (c.ar && normalize(c.ar).includes(q))
    );
  }, [region, search]);

  const filteredAreas = useMemo(() => {
    if (!city || !city.areas) return [];
    const q = normalize(search);
    // iter335 — Areas may be strings (legacy / unmigrated countries)
    // OR `{name, ar}` objects (Sudan + future countries). Coerce to a
    // unified `{name, ar}` shape so the rest of the picker code can
    // treat them uniformly.
    const asNodes = city.areas.map((a) =>
      typeof a === 'string' ? { name: a, ar: '' } : { name: a.name || '', ar: a.ar || '' }
    );
    if (!q) return asNodes;
    return asNodes.filter(
      (a) => normalize(a.name).includes(q) || (a.ar && normalize(a.ar).includes(q))
    );
  }, [city, search]);

  const pickCountry = (c) => {
    setDraft((d) => ({ ...d, country: c.countryCode, region: '', city: '', area: '' }));
    setSearch('');
  };
  const pickRegion = (r) => {
    setDraft((d) => ({ ...d, region: r.name, city: '', area: '' }));
    setSearch('');
  };
  const pickCity = (c) => {
    setDraft((d) => ({ ...d, city: c.name, area: '' }));
    setSearch('');
  };
  const pickArea = (a) => {
    // iter335 — `a` is now always a `{name, ar}` node thanks to the
    // coercion above. Persist the English name into draft.area
    // (canonical key) so the backend / filters stay locale-neutral.
    setDraft((d) => ({ ...d, area: a.name }));
  };
  // iter337 — Free-text fallback for level 4 (City/Town/Village) when
  // the catalog has no matching area, plus the optional level-5
  // "Extra location details" field that follows the cascade.
  const typeCityTown = (v) => {
    setDraft((d) => ({ ...d, area: (v || '').trim() }));
  };
  const typeExtra = (v) => {
    setDraft((d) => ({ ...d, extra: (v || '').slice(0, 200) }));
  };

  const requestGps = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGpsStatus('denied');
      return;
    }
    setGpsStatus('requesting');
    setGpsBanner(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setGpsStatus('granted');
        let detected = { country: '', region: '', city: '', area: '' };
        try {
          const geo = await runReverseGeocode(lat, lng, isRTL ? 'ar' : 'en');
          const code = matchCountry(geo?.countryCode, geo?.countryName);
          const regionName = matchRegionName(code, geo?.principalSubdivision || geo?.region);
          const cityName = matchCityName(code, regionName, geo?.city || geo?.locality);
          detected = { country: code, region: regionName, city: cityName, area: '' };
        } catch (_) { /* offline / ad-blocker → keep draft empty */ }
        setDraft((d) => ({
          ...d,
          ...detected,
          latitude: lat,
          longitude: lng,
          locationSource: 'gps',
          locationConfirmed: false, // user MUST still tap Apply.
        }));
        setGpsBanner({ tone: 'success', parts: detected, lat, lng });
      },
      (err) => {
        setGpsStatus(err?.code === 1 ? 'denied' : 'error');
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60 * 1000 }
    );
  };

  const handleApply = () => {
    // iter337 — All 4 levels are required for publish; the picker's
    // Apply button is also disabled when any level is empty (see
    // `canApply` below) so this is a belt-and-braces guard.
    if (!draft.country || !draft.region || !draft.city || !draft.area) return;
    const label = formatLocationLabel(draft, isRTL ? 'ar' : 'en');
    onApply({
      ...draft,
      locationSource: draft.locationSource || 'manual',
      locationConfirmed: true,
      locationLabel: label,
    });
  };

  const handleClear = () => {
    setDraft({ ...EMPTY });
    setSearch('');
    setGpsBanner(null);
    if (typeof onClear === 'function') onClear();
  };

  if (!open) return null;

  // Cascade stage.
  let stage = 'country';
  if (draft.country) stage = 'region';
  if (draft.country && draft.region) stage = 'city';
  if (draft.country && draft.region && draft.city) stage = 'area';

  // iter337 — Apply requires all FOUR levels (country, region, city, area).
  // Extra is optional and never gates Apply.
  const canApply = Boolean(draft.country && draft.region && draft.city && draft.area);

  // iter322 — Portal to document.body so the sheet ESCAPES any
  // ancestor stacking context (e.g. .products-feed { z-index:1 }).
  // Without this the picker's footer (Apply/Cancel/Clear) is
  // intercepted by BottomNav at 420×900 even with a high z-index.
  const sheetNode = (
    <div
      className="location-picker-sheet"
      data-testid={testid}
      data-stage={stage}
      role="dialog"
      aria-modal="true"
      aria-label={t('locationSelector.title')}
    >
      <div
        className="location-picker-sheet__backdrop"
        data-testid={`${testid}-backdrop`}
        onClick={onClose}
      />
      <div
        className="location-picker-sheet__panel"
        ref={sheetRef}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <header className="location-picker-sheet__header">
          <h3>{t('locationSelector.title')}</h3>
          <button
            type="button"
            className="location-picker-sheet__icon-btn"
            onClick={onClose}
            data-testid={`${testid}-close`}
            aria-label={t('close')}
          >
            <X size={20} />
          </button>
        </header>

        {/* Pre-permission copy — only shown before the user taps the
            GPS button. Per spec: "short permission screen text before
            asking GPS permission". */}
        {showGpsButton && gpsStatus === 'idle' && !draft.latitude && (
          <p
            className="location-picker-sheet__permission-hint"
            data-testid={`${testid}-permission-hint`}
          >
            {t('locationSelector.permissionHint')}
          </p>
        )}

        {/* iter337 — Public-only safety helper. Operator-mandated copy
            so sellers never paste a private home address. Always shown
            above the search box; harmless in any stage. */}
        <p
          className="location-picker-sheet__public-helper"
          data-testid={`${testid}-public-helper`}
        >
          {t('locationSelector.publicOnlyHelper')}
        </p>

        <div className="location-picker-sheet__search-row">
          <span className="location-picker-sheet__search-icon" aria-hidden="true">
            <Search size={16} />
          </span>
          <input
            type="search"
            className="location-picker-sheet__search"
            placeholder={t('locationSelector.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid={`${testid}-search`}
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        </div>

        {showGpsButton && (
          <button
            type="button"
            className="location-picker-sheet__gps-btn"
            onClick={requestGps}
            disabled={gpsStatus === 'requesting'}
            data-testid={`${testid}-use-current`}
          >
            {gpsStatus === 'requesting' ? (
              <>
                <RefreshCw size={14} className="location-picker-sheet__spin" />
                {t('locationSelector.locating')}
              </>
            ) : (
              <>
                <MapPin size={14} />
                {t('locationSelector.useCurrentLocation')}
              </>
            )}
          </button>
        )}

        {/* GPS confirmation banner. The seller MUST still tap Apply. */}
        {gpsBanner && gpsBanner.tone === 'success' && (
          <div
            className="location-picker-sheet__banner location-picker-sheet__banner--ok"
            data-testid={`${testid}-gps-confirm-banner`}
          >
            <CheckCircle2 size={16} />
            <div>
              <strong>{t('locationSelector.gpsDetected')}</strong>
              <div className="location-picker-sheet__banner-detail">
                {formatLocationLabel(gpsBanner.parts, isRTL ? 'ar' : 'en')
                  || (isRTL
                    ? 'تعذّر تحديد الموقع تلقائياً. يرجى الاختيار يدوياً.'
                    : 'Could not auto-detect — please pick below.')}
              </div>
              <div className="location-picker-sheet__banner-tip">
                {t('locationSelector.confirmTip')}
              </div>
            </div>
          </div>
        )}
        {gpsStatus === 'denied' && (
          <div
            className="location-picker-sheet__banner location-picker-sheet__banner--warn"
            data-testid={`${testid}-gps-denied`}
          >
            <AlertCircle size={16} />
            <span>{t('home.nearMe.permissionDenied')}</span>
          </div>
        )}
        {gpsStatus === 'error' && (
          <div
            className="location-picker-sheet__banner location-picker-sheet__banner--warn"
            data-testid={`${testid}-gps-error`}
          >
            <AlertCircle size={16} />
            <span>{t('home.nearMe.locationError')}</span>
          </div>
        )}

        {/* Breadcrumb — tap any crumb to pop back to that level. */}
        <div
          className="location-picker-sheet__crumbs"
          data-testid={`${testid}-crumbs`}
        >
          <button
            type="button"
            className={`location-picker-sheet__crumb${draft.country ? '' : ' location-picker-sheet__crumb--active'}`}
            onClick={() => setDraft({ ...EMPTY })}
            data-testid={`${testid}-crumb-root`}
          >
            {t('locationSelector.allCountries')}
          </button>
          {draft.country && (
            <>
              <ChevronRight size={12} className="location-picker-sheet__crumb-chev" />
              <button
                type="button"
                className={`location-picker-sheet__crumb${draft.region ? '' : ' location-picker-sheet__crumb--active'}`}
                onClick={() => setDraft((d) => ({ ...d, region: '', city: '', area: '' }))}
                data-testid={`${testid}-crumb-country`}
              >
                {isRTL ? (country?.countryNameAr || draft.country) : (country?.countryName || draft.country)}
              </button>
            </>
          )}
          {draft.region && (
            <>
              <ChevronRight size={12} className="location-picker-sheet__crumb-chev" />
              <button
                type="button"
                className={`location-picker-sheet__crumb${draft.city ? '' : ' location-picker-sheet__crumb--active'}`}
                onClick={() => setDraft((d) => ({ ...d, city: '', area: '' }))}
                data-testid={`${testid}-crumb-region`}
              >
                {isRTL && region?.ar ? region.ar : draft.region}
              </button>
            </>
          )}
          {draft.city && (
            <>
              <ChevronRight size={12} className="location-picker-sheet__crumb-chev" />
              <button
                type="button"
                className={`location-picker-sheet__crumb${draft.area ? '' : ' location-picker-sheet__crumb--active'}`}
                onClick={() => setDraft((d) => ({ ...d, area: '' }))}
                data-testid={`${testid}-crumb-city`}
              >
                {isRTL && city?.ar ? city.ar : draft.city}
              </button>
            </>
          )}
          {draft.area && (
            <>
              <ChevronRight size={12} className="location-picker-sheet__crumb-chev" />
              <span
                className="location-picker-sheet__crumb location-picker-sheet__crumb--active"
                data-testid={`${testid}-crumb-area`}
              >
                {draft.area}
              </span>
            </>
          )}
        </div>

        {/* Cascading list — body renders the current level. */}
        <div className="location-picker-sheet__body" data-testid={`${testid}-list`}>
          {stage === 'country' && (
            <>
              <h4 className="location-picker-sheet__list-title">
                {t('locationSelector.selectCountry')}
              </h4>
              <ul className="location-picker-sheet__list">
                {filteredCountries.length === 0 && (
                  <li className="location-picker-sheet__empty">
                    {t('locationSelector.noMatch')}
                  </li>
                )}
                {filteredCountries.map((c) => (
                  <li key={c.countryCode}>
                    <button
                      type="button"
                      className="location-picker-sheet__row"
                      onClick={() => pickCountry(c)}
                      data-testid={`${testid}-country-${c.countryCode}`}
                    >
                      <span>{isRTL ? c.countryNameAr : c.countryName}</span>
                      <ChevronRight size={14} className="location-picker-sheet__row-chev" />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {stage === 'region' && country && (
            <>
              <h4 className="location-picker-sheet__list-title">
                {t('locationSelector.selectRegion')}
              </h4>
              <ul className="location-picker-sheet__list">
                {filteredRegions.length === 0 && (
                  <li className="location-picker-sheet__empty">
                    {t('locationSelector.noMatch')}
                  </li>
                )}
                {filteredRegions.map((r) => (
                  <li key={r.name}>
                    <button
                      type="button"
                      className="location-picker-sheet__row"
                      onClick={() => pickRegion(r)}
                      data-testid={`${testid}-region-${r.name.replace(/\s+/g, '-')}`}
                    >
                      <span>{isRTL && r.ar ? r.ar : r.name}</span>
                      <ChevronRight size={14} className="location-picker-sheet__row-chev" />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {stage === 'city' && region && (
            <>
              <h4 className="location-picker-sheet__list-title">
                {t('locationSelector.selectCity')}
              </h4>
              <ul className="location-picker-sheet__list">
                {filteredCities.length === 0 && (
                  <li className="location-picker-sheet__empty">
                    {t('locationSelector.noMatch')}
                  </li>
                )}
                {filteredCities.map((c) => (
                  <li key={c.name}>
                    <button
                      type="button"
                      className="location-picker-sheet__row"
                      onClick={() => pickCity(c)}
                      data-testid={`${testid}-city-${c.name.replace(/\s+/g, '-')}`}
                    >
                      <span>{isRTL && c.ar ? c.ar : c.name}</span>
                      <ChevronRight size={14} className="location-picker-sheet__row-chev" />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {stage === 'area' && city && (
            <>
              <h4 className="location-picker-sheet__list-title">
                {t('locationSelector.selectArea')}
              </h4>
              <ul className="location-picker-sheet__list">
                {filteredAreas.length === 0 && (
                  <li className="location-picker-sheet__empty">
                    {t('locationSelector.noMatch')}
                  </li>
                )}
                {filteredAreas.map((a) => (
                  <li key={a.name}>
                    <button
                      type="button"
                      className={`location-picker-sheet__row${draft.area === a.name ? ' location-picker-sheet__row--selected' : ''}`}
                      onClick={() => pickArea(a)}
                      data-testid={`${testid}-area-${(a.name || '').replace(/\s+/g, '-')}`}
                    >
                      <span>{isRTL && a.ar ? a.ar : a.name}</span>
                      {draft.area === a.name && (
                        <Check size={14} className="location-picker-sheet__row-check" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
              {/* iter337 — Free-text fallback. Some catalog rows have no
                  pre-filled towns/villages (operator spec leaves
                  smaller countries shallow). When the seller cannot
                  find a match they type their City/Town/Village here
                  and the value is persisted into `draft.area` — same
                  field the structured rows write to, so downstream
                  filters keep working. */}
              <div
                className="location-picker-sheet__type-row"
                data-testid={`${testid}-type-city-town-row`}
              >
                <label className="location-picker-sheet__type-label">
                  {t('locationSelector.cityTownTypeHint')}
                </label>
                <input
                  type="text"
                  className="location-picker-sheet__type-input"
                  placeholder={t('locationSelector.cityTownTypePlaceholder')}
                  value={draft.area || ''}
                  onChange={(e) => typeCityTown(e.target.value)}
                  data-testid={`${testid}-type-city-town`}
                  maxLength={80}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>
            </>
          )}
        </div>

        {/* iter337 — Level-5 OPTIONAL "Extra location details" input.
            Always visible once the cascade has reached level 4 so the
            seller can add a market / mall / street / landmark without
            opening a separate screen. Display only — NOT used as a
            primary search/nearby filter (operator spec). */}
        {draft.country && draft.region && draft.city && (
          <div
            className="location-picker-sheet__extra"
            data-testid={`${testid}-extra-row`}
          >
            <label
              htmlFor={`${testid}-extra-input`}
              className="location-picker-sheet__extra-label"
            >
              {t('locationSelector.extraDetailsLabel')}
            </label>
            <input
              id={`${testid}-extra-input`}
              type="text"
              className="location-picker-sheet__extra-input"
              placeholder={t('locationSelector.extraDetailsPlaceholder')}
              value={draft.extra || ''}
              onChange={(e) => typeExtra(e.target.value)}
              data-testid={`${testid}-extra-input`}
              maxLength={200}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
        )}

        {/* iter337 — Required-levels banner shown while Apply is
            disabled. Mirrors the publish-side error message exactly
            so the seller sees consistent copy. */}
        {!canApply && (draft.country || draft.region || draft.city) && (
          <p
            className="location-picker-sheet__required-hint"
            data-testid={`${testid}-required-hint`}
          >
            {t('locationSelector.completeRequired')}
          </p>
        )}

        <footer className="location-picker-sheet__footer">
          <button
            type="button"
            className="location-picker-sheet__ghost-btn"
            onClick={handleClear}
            data-testid={`${testid}-clear`}
          >
            <Trash2 size={14} />
            <span>{t('locationSelector.clear')}</span>
          </button>
          <button
            type="button"
            className="location-picker-sheet__ghost-btn"
            onClick={onClose}
            data-testid={`${testid}-cancel`}
          >
            {t('locationSelector.cancel')}
          </button>
          <button
            type="button"
            className="location-picker-sheet__apply-btn"
            onClick={handleApply}
            disabled={!canApply}
            data-testid={`${testid}-apply`}
          >
            <Check size={14} />
            <span>{t('locationSelector.apply')}</span>
          </button>
        </footer>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return sheetNode;
  return createPortal(sheetNode, document.body);
}
